import axios from '@/axios-client.js';

// =====================================================
// Helper function to add any image to PDF
// Handles logos (contain) and photos (cover)
// =====================================================
export function addImageToPdf(
  pdf,
  img,               // HTMLImageElement or canvas
  x,
  y,
  boxW,
  boxH,
  {
    mode = 'contain', // 'contain' = logo/map, 'cover' = photo
    maxSize = 512,    // max canvas size
    quality = 0.85,   // JPEG quality
    fillWhite = true  // fill background with white
  } = {}
) {
  // ----- Calculate resize -----
  let targetW = img.width;
  let targetH = img.height;

  if (img.width > img.height) {
    targetW = Math.min(img.width, maxSize);
    targetH = targetW / (img.width / img.height);
  } else {
    targetH = Math.min(img.height, maxSize);
    targetW = targetH * (img.width / img.height);
  }

  // ----- Canvas -----
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(targetW);
  canvas.height = Math.round(targetH);

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Fill white if necessary
  if (fillWhite) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // ----- Crop or center -----
  let sx = 0, sy = 0, sw = img.width, sh = img.height;

  const imgRatio = img.width / img.height;
  const boxRatio = boxW / boxH;

  if (mode === 'cover') {
    if (imgRatio > boxRatio) {
      // Landscape → crop horizontal
      sh = img.height;
      sw = sh * boxRatio;
      sx = (img.width - sw) / 2;
    } else {
      // Portrait → crop vertical
      sw = img.width;
      sh = sw / boxRatio;
      sy = (img.height - sh) / 2;
    }
  }

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  // ----- Calculate placement for 'contain' mode -----
  let drawW = boxW, drawH = boxH;
  let drawX = x, drawY = y;

  if (mode === 'contain') {
    const canvasRatio = canvas.width / canvas.height;
    if (canvasRatio > boxRatio) {
      drawW = boxW;
      drawH = boxW / canvasRatio;
    } else {
      drawH = boxH;
      drawW = boxH * canvasRatio;
    }
    drawX = x + (boxW - drawW) / 2;
    drawY = y + (boxH - drawH) / 2;
  }

  // ----- Export format depending on mode -----
  let dataUrl;
  let format;

  if (mode === 'cover') {
    // Photos → PNG to preserve colors and sharpness
    dataUrl = canvas.toDataURL('image/png');
    format = 'PNG';
  } else {
    // Logo / map → JPEG to reduce size
    dataUrl = canvas.toDataURL('image/jpeg', quality);
    format = 'JPEG';
  }

  pdf.addImage(dataUrl, format, drawX, drawY, drawW, drawH);
}

// =====================================================
// Load an image from URL and return an optimized canvas
// =====================================================
export async function loadImage(url) {
  try {
    const response = await axios.get(url, { responseType: 'blob' });

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = URL.createObjectURL(response.data);
    });
  } catch (error) {
    console.error('Error loading image:', error);
    throw error;
  }
}

// =====================================================
// Convert hex color to RGB array
// =====================================================
export function hexToRgb(hex) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;

  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ];
}

// =====================================================
// Normalize text: remove line breaks and extra spaces
// =====================================================
function normalizeText(text) {
  return String(text)
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// =====================================================
// Ellipsize text to fit in PDF width
// =====================================================
function ellipsizeText(pdf, text, maxWidth) {
  if (!text) return '';

  const normalized = normalizeText(text);
  if (pdf.getTextWidth(normalized) <= maxWidth) return normalized;

  const ellipsis = '…';
  let result = normalized;
  while (result.length > 0 && pdf.getTextWidth(result + ellipsis) > maxWidth) {
    result = result.slice(0, -1);
  }
  return result + ellipsis;
}

// =====================================================
// Generate a clean, readable filename for the PDF export
// =====================================================
export function generatePdfFilename({
  featuresCountInFile,
  featureTitle,
  projectTitle,
  featureTypeTitle,
  batchIndex = null,
  totalBatches = null,
}) {
  // Clean string for filenames (remove accents, special chars, keep letters/numbers)
  const clean = (str = '') => {
    str = str.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')  // remove accents
      .replace(/[^a-z0-9]/gi, '_');     // replace non-alphanum with underscore

    // trim leading underscores
    let start = 0;
    while (str[start] === '_') start++;
    
    // trim trailing underscores
    let end = str.length;
    while (str[end - 1] === '_') end--;

    return str.slice(start, end);
  };

  // Capitalize first letter
  const capitalize = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());

  const nameParts = [];

  // Project title is always included
  if (projectTitle) nameParts.push(capitalize(clean(projectTitle)));

  // Feature type title is always included
  if (featureTypeTitle) {
    nameParts.push(capitalize(clean(featureTypeTitle)));
  }
  // Single feature in the file → include its title
  if (featuresCountInFile === 1 && featureTitle) {
    nameParts.push(capitalize(clean(featureTitle)));
  }

  // Only include lot info if multiple features in file AND multiple batches
  if (featuresCountInFile > 1 && totalBatches && totalBatches > 1 && batchIndex != null) {
    nameParts.push(`Part_${batchIndex}-${totalBatches}`);
  }

  return nameParts.join('-') + '.pdf';
}

// =====================================================
// Build PDF page matrix (logo, title, attributes, map, photos)
// =====================================================
export function buildPdfMatrix(
  pdf,
  {
    ftTitle,
    title,
    createdDate,
    attributes,
    mapCanvas,
    photos = [],
    themeColor,
    logoImg,
    index,
    total
  }
) {
  const colorBlack = 0;
  const colorDarkGrey = 50;
  const colorGrey = 100;
  const colorLightGrey = 200;
  const margin = 10;
  const fullWidth = 210;
  const pageHeight = 297;
  const width = fullWidth - 2 * margin;

  let height = margin;

  // ===== LOGO =====
  const logoX = margin + (width - 40) / 2;
  const logoY = height;
  pdf.setDrawColor(180);
  pdf.setTextColor(colorBlack);

  if (logoImg) {
    addImageToPdf(pdf, logoImg, logoX, logoY, 40, 40, { mode: 'contain', quality: 0.9 });
  } else {
    pdf.rect(logoX, logoY, 40, 40);
    pdf.text('GEOCONTRIB', 105, height + 23, { align: 'center' });
  }

  height += 45;

  // ===== FEATURE TYPE =====
  pdf.setFontSize(16);
  pdf.setFont(undefined, 'bold');
  pdf.text(ftTitle, 105, height + 6, { align: 'center' });

  // ===== FEATURE COUNT =====
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.text(`SIGNALEMENT ${index + 1} / ${total}`, 105, height + 12, { align: 'center' });

  height += 22;

  // ===== TITLE + DATE =====
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  const titleText = ellipsizeText(pdf, title, width - 40); // max width before date

  pdf.text(titleText, margin, height + 10);

  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(colorGrey);
  pdf.text('Créé le ' + createdDate, margin + width, height + 10, { align: 'right' });

  height += 15;

  // ===== LINE =====
  pdf.setDrawColor(...themeColor);
  pdf.setLineWidth(1.5);
  pdf.line(margin, height, width + margin, height);

  // ===== BODY =====
  const bodyHeight = 120;
  let attrWidth = 115;
  const mapGap = 5;
  const mapSize = 70;
  if (!mapCanvas) { // If no map, use the whole row for attributes
    attrWidth += (mapGap + mapSize);
  } 
  const maxAllowedAttr = 5;       // Maximum allowed attributes
  const attrStartY = height + 5;  // Attribute start Y position
  const attrRowHeight = mapSize / maxAllowedAttr; // Attribute row height
  const attrTextYPadding = 7;     // Attribute text Y padding
  const attrX = margin;           // Attribute X position
  const keyX = attrX;             // Key X position
  const valueX = attrX + 40;      // Value X position (space after key)
  const keyMaxWidth = 35;         // Maximum key width
  const valueMaxWidth = attrWidth - 40; // Maximum value width (remaining space before map)

  attributes.forEach((attr, i) => {
    const attrTop = attrStartY + i * attrRowHeight;
    const textY = attrTop + attrTextYPadding;

    pdf.setFontSize(11);
    
    // Key
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(colorDarkGrey);
    pdf.text(ellipsizeText(pdf, attr.label, keyMaxWidth), keyX, textY);

    // Value
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(colorGrey);
    pdf.text(ellipsizeText(pdf, attr.value, valueMaxWidth), valueX, textY);

    // Separator line (except after the last if there is more than one attribute)
    if (attributes.length === 1 || i < attributes.length - 1) {
      const lineY = attrTop + attrRowHeight / 1.15; // 1.15 is an arbitrary value;
      // since the center of lines cannot be precisely determined in PDF
      
      pdf.setDrawColor(colorLightGrey);
      pdf.setLineWidth(0.4);
      pdf.line(attrX, lineY, attrX + attrWidth, lineY);
    }
  });

  // ===== MAP =====
  if (mapCanvas) {
    addImageToPdf(pdf, mapCanvas, margin + attrWidth + mapGap, height + 5, mapSize, mapSize, {
      mode: 'contain',
      quality: 0.85
    });
  }

  height += bodyHeight;

  // ===== PHOTOS =====
  const marginBottom = 5;
  const photoZoneHeight = 100;
  const photoTop = pageHeight - marginBottom - photoZoneHeight;

  pdf.setFillColor(240);
  pdf.rect(0, photoTop, fullWidth, photoZoneHeight, 'F');

  const photoWidth = (width - 10) / 3;
  const photoHeight = 80;

  photos.slice(0, 3).forEach((img, i) => {
    const x = margin + i * (photoWidth + 5);
    const y = photoTop + 10;
    addImageToPdf(pdf, img, x, y, photoWidth, photoHeight, {
      mode: 'cover',
      maxSize: 1024
    });
  });

  // ===== BOTTOM PAGE LINE =====
  pdf.setDrawColor(...themeColor);
  pdf.setLineWidth(0.6);
  pdf.line(0, pageHeight - marginBottom, fullWidth, pageHeight - marginBottom);
}
